import type { ReactNode } from 'react';
import type { TripView } from '@voll-intelligence/domain';
import {
  firstStopByService,
  groupStopsByDay,
  isEmptiedTrip,
  liveStops,
  type TripStop,
  type TripStopDay,
} from './derive.js';
import { t } from './i18n.js';
import type { ItineraryAddons } from './itinerary_addons.js';
import { dayHeading } from './labels.js';
import {
  byDayKey,
  dayKey,
  provisionalDays,
  type ProvisionalDay,
  type ProvisionalRow,
} from './provisional.js';
import {
  ProvisionalClose,
  ProvisionalStop,
  provisionalStatusLabel,
} from './provisional_itinerary.js';
import type { BookingReceipt } from './receipt_types.js';
import { SlotRow, type ItinerarySlot } from './slot_row.js';
import { StopRow, type ItineraryLayout } from './stop_row.js';

export { ProvisionalItinerary } from './provisional_itinerary.js';
export type {
  ItineraryAddon,
  ItineraryAddonState,
  ItineraryAddons,
  ItineraryDirection,
} from './itinerary_addons.js';
export type { ItineraryLayout } from './stop_row.js';
export type { ItinerarySlot } from './slot_row.js';

/** O itinerário por dia. O hero "A seguir" é irmão dele, não parte: ele fica FORA da
 *  rolagem (é o destaque fixo da lateral) e só o itinerário rola.
 *
 *  Duas fontes, e nunca a mesma perna nas duas: os passos EMITIDOS (o `TripView`) e,
 *  enquanto o voucher do modal não chegou, as linhas do recibo da bancada. As duas
 *  dividem a MESMA linha do tempo — um dia por data, em ordem —, e dentro do dia o
 *  provisório entra DEPOIS dos emitidos: o documento é a autoridade. A frase de vazio
 *  só sobra quando não há nem um nem outro. */

/** O traje das frases de vazio do itinerário (#2548): a MESMA moldura do
 *  `EmptyTripSlot` do kit (fio, raio xl, 24 de padding, detalhe em muted) — o
 *  designer pediu um BLOCO acima dos selos de wallet, para os selos terem no que se
 *  apoiar; uma frase solta de 13px não sustentava. O kit não serve aqui porque o
 *  slot dele nasce com a ação de arquivar, e estas frases não têm ação. Utilitárias
 *  do DS, válidas porque o `.tp` é raiz `genui2`. */
const ITIN_EMPTY_BLOCK =
  'm-0 grid place-items-center rounded-xl border p-6 text-center text-detail text-muted-foreground';

/** O itinerário da viagem — passos vivos + as linhas provisórias do recibo, com o
 *  estado vazio. Exportado (#2205) porque o painel da CONVERSA mostra exatamente
 *  esta lista: extrair um organismo paralelo duplicaria a régua de quais status
 *  entram, quem carrega o preço e quando o provisório aparece. */
export function TripItinerary({
  trip,
  provisional,
  onOpenVoucher,
  addons,
  authenticated,
  renderEmptied,
  renderEmpty,
  layout = 'cards',
  slots = [],
}: Readonly<{
  trip: TripView;
  /** O recibo DESTA viagem já pelo portão do painel (posse + ausência de voo vivo
   *  checadas ali, para o MESMO portão valer aqui e na régua de meta), ou null. */
  provisional?: BookingReceipt | null;
  onOpenVoucher: (serviceKey: string) => void;
  /** Os addons que o host conduz (ver `ItineraryAddons`). Opcional: sem ele, o card do voo
   *  só mostra o assento e a bagagem que o bilhete já traz. */
  addons?: ItineraryAddons;
  authenticated: boolean;
  /** O slot de "viagem esvaziada" do host (web-chat: `EmptiedTripSlot`). Sem ele,
   *  a viagem esvaziada cai na frase neutra de vazio. */
  renderEmptied?: (trip: TripView) => ReactNode;
  /** O slot do vazio de sempre — a viagem que ainda não tem serviço — do host
   *  (web-chat: `EmptyItinerarySlot`, a mala aberta, #4025). A arte é do host: o
   *  pacote não importa asset. Sem ele, cai na frase neutra (o operador). */
  renderEmpty?: () => ReactNode;
  /** A cara do itinerário (ver `ItineraryLayout`): o console no card de sempre, o
   *  drawer da conversa na timeline (#6328) e a página da viagem na timeline com o
   *  card elevado (#6313). */
  layout?: ItineraryLayout;
  /** Os passos AINDA NÃO reservados que o host oferece entre os trechos (#6314), cada um no
   *  dia e na hora dele. Só a página da viagem os passa. */
  slots?: readonly ItinerarySlot[];
}>) {
  // `liveStops`/`firstStopByService` moraram AQUI até a Task 5 (#2205): a mala do
  // topo (`tripBagCount`, `ConversationTripPanel.tsx`) precisava da MESMA régua
  // fora do render, e duas cópias divergiriam no primeiro ajuste de status morto
  // — extraídas para `derive.ts` (achado do review).
  const stops = liveStops(trip);
  const days = groupStopsByDay(stops);
  // O preço mora no PRIMEIRO passo do serviço (a unidade de preço é o bilhete,
  // não o trecho — a mesma régua do provisório): a volta não repete o valor.
  const pricedKeys = new Set([...firstStopByService(stops).values()].map((stop) => stop.key));
  // Derivado UMA vez: as linhas provisórias E as duas decisões que dependem delas (o
  // bloco entra, a frase de vazio sai) leem esta mesma contagem — nunca a existência
  // do recibo. Um recibo que não vira linha nenhuma em `provisionalDays`
  // (`provisional.ts`) não pode deixar o slot sem o bloco E sem a
  // frase: as duas leriam a mesma pergunta errada ("há recibo?") se não fosse por isto.
  const provisionalRows = provisional ? provisionalDays(provisional) : [];
  const statusLabel = provisional ? provisionalStatusLabel(provisional) : '';
  return (
    <section
      className={layout === 'cards' ? 'tp-itin' : `tp-itin tp-itin--timeline tp-itin--${layout}`}
      aria-label={t('trips.panel.itinerary')}
    >
      {itineraryDays(days, provisionalRows, slots).map((day) => (
        <div className="tp-day" key={day.key}>
          <h3 className="tp-day__ttl">{day.heading}</h3>
          {day.steps.map((step, i) => {
            const linked = i < day.steps.length - 1 || day.rows.length > 0;
            return step.kind === 'slot' ? (
              <SlotRow key={step.slot.key} slot={step.slot} linked={linked} />
            ) : (
              <StopRow
                key={step.stop.key}
                stop={step.stop}
                linked={linked}
                priced={pricedKeys.has(step.stop.key)}
                onOpenVoucher={onOpenVoucher}
                addons={addons}
                authenticated={authenticated}
                layout={layout}
              />
            );
          })}
          {day.rows.length > 0 && (
            // A fronteira entre DOCUMENTO e PROMESSA existe no DOM, não só no tracejado:
            // sem ela, quem usa leitor de tela ouve as duas como itinerário emitido.
            <div className="tp-day__prov" role="group" aria-label={t('trips.provisional.label')}>
              {day.rows.map((row, i) => (
                <ProvisionalStop
                  key={row.key}
                  row={row}
                  linked={i < day.rows.length - 1}
                  statusLabel={statusLabel}
                />
              ))}
            </div>
          )}
        </div>
      ))}
      {provisional && provisionalRows.length > 0 && <ProvisionalClose receipt={provisional} />}
      {days.length === 0 &&
        provisionalRows.length === 0 &&
        // DEGRADADA vem antes de tudo: `servicesDegraded` (trip-api) diz que ≥1
        // voucher falhou no read-through — os serviços existem, só não vieram.
        // Nem a frase de vazio (mentira) nem o slot de arquivar (a viagem tem
        // conteúdo) servem aqui. Flag ausente = backend velho = fluxo de sempre.
        (trip.servicesDegraded ? (
          <p className={ITIN_EMPTY_BLOCK}>{t('trips.services.error')}</p>
        ) : // ESVAZIADA (zero vouchers E zero modals) oferece o arquivamento — o
        // protótipo do Mover. Itinerário vazio com orders ou serviços mortos na
        // viagem segue no vazio de sempre (o slot do host, senão a frase neutra):
        // arquivar ali seria um 409 no clique.
        isEmptiedTrip(trip) && renderEmptied ? (
          renderEmptied(trip)
        ) : renderEmpty ? (
          renderEmpty()
        ) : (
          <p className={ITIN_EMPTY_BLOCK}>{t('trips.services.empty')}</p>
        ))}
    </section>
  );
}

/** Um dia do itinerário com as DUAS fontes: os passos emitidos e as linhas do recibo
 *  que caem na mesma data. */
interface ItineraryDay {
  key: string;
  heading: string;
  /** Os passos emitidos e os slots do host, na ordem da hora (o passo sem hora fica onde
   *  estava). */
  steps: ItineraryStep[];
  rows: ProvisionalRow[];
}

type ItineraryStep = { kind: 'stop'; stop: TripStop } | { kind: 'slot'; slot: ItinerarySlot };

const stepTime = (step: ItineraryStep): string =>
  step.kind === 'slot' ? step.slot.time : step.stop.time;

/** Encaixa cada slot depois do último passo do dia que começa até a hora dele. */
function withSlots(stops: readonly TripStop[], slots: readonly ItinerarySlot[]): ItineraryStep[] {
  const steps: ItineraryStep[] = stops.map((stop) => ({ kind: 'stop', stop }));
  for (const slot of [...slots].sort((a, b) => a.time.localeCompare(b.time))) {
    let after = -1;
    steps.forEach((step, i) => {
      if (stepTime(step) !== '' && stepTime(step) <= slot.time) after = i;
    });
    steps.splice(after + 1, 0, { kind: 'slot', slot });
  }
  return steps;
}

/** `YYYY-MM-DD` → o dia à meia-noite local, para o cabeçalho de um dia que só tem slot. */
function localDay(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Os dias emitidos e os do recibo numa linha do tempo SÓ, pela mesma chave de dia
 *  (`dayKey`). O bloco provisório vinha inteiro DEPOIS dos emitidos, e o hotel reservado
 *  depois do voo repetia o dia da volta e aparecia depois dele ("Qui 19", "Qua 18",
 *  "Qui 19"). */
function itineraryDays(
  live: TripStopDay[],
  provisional: ProvisionalDay[],
  slots: readonly ItinerarySlot[],
): ItineraryDay[] {
  const byKey = new Map<string, { heading: string; stops: TripStop[]; rows: ProvisionalRow[] }>();
  const dayOf = (key: string, heading: string) => {
    const known = byKey.get(key);
    if (known) return known;
    const day = { heading, stops: [] as TripStop[], rows: [] as ProvisionalRow[] };
    byKey.set(key, day);
    return day;
  };
  for (const day of live) {
    const heading = day.date ? dayHeading(day.date) : t('trips.services.noDate');
    dayOf(dayKey(day.date), heading).stops.push(...day.stops);
  }
  for (const day of provisional) dayOf(day.key, day.heading).rows.push(...day.rows);
  for (const slot of slots) dayOf(slot.day, dayHeading(localDay(slot.day)));
  return [...byKey.entries()]
    .map(([key, day]) => ({
      key,
      heading: day.heading,
      steps: withSlots(
        day.stops,
        slots.filter((slot) => slot.day === key),
      ),
      rows: day.rows,
    }))
    .sort(byDayKey);
}
